/**
 * 菜单管理组合式函数
 * 合并了原 menu.ts 和 useMenu.ts 的功能
 */

import { computed } from 'vue'
import { useRouter, type RouteRecordRaw } from 'vue-router'
import type { MenuItem } from '@/types'
import MainLayout from '@/layouts/MainLayout.vue'

/**
 * 从路由配置生成菜单树
 * @param routes 路由配置数组
 * @param basePath 基础路径
 * @returns 菜单项数组
 */
function generateMenuFromRoutes(
  routes: readonly RouteRecordRaw[],
  basePath = ''
): MenuItem[] {
  const menuItems: MenuItem[] = []

  routes.forEach((route) => {
    // 跳过隐藏的路由
    if (route.meta?.hidden) {
      return
    }

    // 构建完整路径，确保路径以 / 开头
    const fullPath = basePath ? `${basePath}/${route.path}` : `/${route.path}`
    const normalizedPath = fullPath.replace(/\/+/g, '/') // 处理多余的斜杠

    // 处理有子路由的情况
    if (route.children && route.children.length > 0) {
      // 过滤出可见的子路由
      const visibleChildren = route.children.filter((child) => !child.meta?.hidden)

      // 如果只有一个子路由且没有设置 alwaysShow，直接显示子路由
      if (visibleChildren.length === 1 && !route.meta?.alwaysShow) {
        const child = visibleChildren[0]
        const childPath = child.path.startsWith('/')
          ? child.path
          : `${normalizedPath}/${child.path}`.replace(/\/+/g, '/')

        menuItems.push({
          path: childPath,
          title: child.meta?.title || (child.name as string) || '',
          icon: route.meta?.icon || child.meta?.icon, // 优先使用父路由图标
        })
      } else if (visibleChildren.length > 0) {
        // 多个子路由，创建父菜单
        const menuItem: MenuItem = {
          path: normalizedPath,
          title: route.meta?.title || (route.name as string) || '',
          icon: route.meta?.icon,
          children: [],
        }

        // 递归生成子菜单
        visibleChildren.forEach((child) => {
          const childPath = child.path.startsWith('/')
            ? child.path
            : `${normalizedPath}/${child.path}`.replace(/\/+/g, '/')

          menuItem.children!.push({
            path: childPath,
            title: child.meta?.title || (child.name as string) || '',
          })
        })

        menuItems.push(menuItem)
      }
    } else if (route.meta?.title) {
      // 没有子路由的顶级菜单
      menuItems.push({
        path: normalizedPath,
        title: route.meta.title,
        icon: route.meta.icon,
      })
    }
  })

  // 返回菜单项，保持定义顺序（移除了 sort 排序逻辑）
  return menuItems
}

/**
 * 获取所有需要缓存的页面名称
 * @param routes 路由配置数组
 * @returns 需要缓存的页面名称数组
 */
function getCachedPages(routes: readonly RouteRecordRaw[]): string[] {
  const cached: string[] = []

  function traverse(routes: readonly RouteRecordRaw[]) {
    routes.forEach((route) => {
      if (route.meta?.keepAlive && route.name) {
        cached.push(route.name as string)
      }
      if (route.children) {
        traverse(route.children)
      }
    })
  }

  traverse(routes)
  return cached
}

export const useMenu = () => {
  const router = useRouter()
  const routes = router.options.routes

  // 生成菜单数据
  const menuList = computed<MenuItem[]>(() => {
    // 找到使用 MainLayout 的主路由
    const mainRoute = routes.find((route) => route.component === MainLayout)

    // 从主路由的 children 生成菜单
    return mainRoute ? generateMenuFromRoutes(mainRoute.children || []) : []
  })

  // 获取需要缓存的页面
  const cachedPages = computed(() => {
    return getCachedPages(routes)
  })

  // 根据当前路径获取面包屑
  const getBreadcrumbs = (path: string) => {
    const breadcrumbs: Array<{ path: string; title: string }> = []
    const segments = path.split('/').filter(Boolean)
    let currentPath = ''

    // 查找匹配的路由配置
    segments.forEach((segment) => {
      currentPath += `/${segment}`

      // 在路由配置中查找对应的标题
      let title = segment

      // 遍历所有路由查找匹配的meta.title
      const findTitle = (routes: any[], searchPath: string): string | undefined => {
        for (const route of routes) {
          const routePath = route.path.startsWith('/') ? route.path : `/${route.path}`

          if (routePath === searchPath || routePath === searchPath.replace(/\/$/, '')) {
            return route.meta?.title
          }

          if (route.children) {
            const childTitle = findTitle(route.children, searchPath)
            if (childTitle) return childTitle
          }
        }
        return undefined
      }

      const foundTitle = findTitle(routes as any[], currentPath)
      if (foundTitle) {
        title = foundTitle
      }

      breadcrumbs.push({
        path: currentPath,
        title,
      })
    })

    return breadcrumbs
  }

  return {
    menuList,
    cachedPages,
    getBreadcrumbs,
  }
}
